import { Multistep, useMultistepApi } from 'informed';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { StepProps } from './type';
import { Button } from '@/components/buttons';
import { FormGroup } from '@/components/form/groups/FormGroup';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { LoaderWrapper } from '@/components/profile/LoaderWrapper';
import { getCountryFlagIcon } from '@/helpers/cdn';
import { notEmpty } from '@/helpers/common';
import { ThemeMode } from '@/helpers/enums';
import { required } from '@/helpers/validators';
import { useCountryState } from '@/hooks';
import useEstonia from '@/hooks/estonia';
import { useServer } from '@/modules/server/context/ServerEnvContext';
import { useCommonStore } from '@/store';

import {
  ScChangeCountryMessage,
  ScRegistrationWrapButtons,
} from '../../../styled';
import { ScFormRegistration } from '@/components/form/groups/styled';

export const AddressInformationStep: FC<StepProps & { isLoading: boolean }> = ({
  step,
  isLoading,
}) => {
  const { tooltipIcon: TooltipIcon } = useTheme();
  const [selectedCountry, setSelectedCountry] = useState('');
  const { next, previous } = useMultistepApi();

  const { t } = useTranslation();
  const { user, countries } = useCommonStore(
    useShallow((state) => ({
      user: state.user,
      countries: state.countries,
    }))
  );

  const { countryCode } = useServer();

  const { isEstonia } = useEstonia();

  const { states, statesPending } = useCountryState(
    user?.country || selectedCountry || countryCode
  );

  const countriesOptions = useMemo(() => {
    return countries.map(({ name, code }) => ({
      name,
      value: code,
      icon: getCountryFlagIcon(code.toLowerCase()),
    }));
  }, [countries]);

  const statesOptions = useMemo(() => {
    if (!states) return [];

    const { fields } = states;

    const stateField = fields.find((item) => item.field === 'state');

    return stateField?.inclusion.in.map((state: string) => ({
      name: state,
      value: state,
    }));
  }, [states]);

  const handleFinishClick = useCallback(() => {
    next();
  }, [next]);

  return (
    <Multistep.Step step={step}>
      <ScFormRegistration>
        {isEstonia && (
          <FormGroup title={t('auth.registration.form.personalIdNumber')}>
            <Input
              name="personalIdNumber"
              validate={required}
              placeholder={t('auth.registration.form.personalIdNumber')}
            />
          </FormGroup>
        )}
        <FormGroup title={t('common:auth.registration.form.country')}>
          <Select
            initialValue={user?.country || countryCode}
            name="country"
            placeholder={t('common:auth.registration.form.country')}
            validate={required}
            options={countriesOptions}
            disabled={notEmpty(user?.country)}
            mode={ThemeMode.Light}
            onSelectOption={(state) => setSelectedCountry(String(state))}
          />
          {user?.country && (
            <ScChangeCountryMessage>
              <TooltipIcon />
              <p className="message">
                <Trans
                  i18nKey="changeCountryMessage"
                  components={{
                    1: <b id="intercomcustomLauncher" />,
                  }}
                />
              </p>
            </ScChangeCountryMessage>
          )}
        </FormGroup>
        <LoaderWrapper loading={statesPending}>
          {statesOptions && (
            <FormGroup title={t('common:auth.registration.form.state')}>
              <Select
                initialValue={statesOptions[0]?.name}
                name="state"
                placeholder={t('common:auth.registration.form.state')}
                validate={(value) =>
                  statesOptions ? required(value, value) : undefined
                }
                options={statesOptions}
                mode={ThemeMode.Light}
                disabled={notEmpty(user?.state)}
              />
            </FormGroup>
          )}
        </LoaderWrapper>
        <FormGroup title={t('common:auth.registration.form.city')}>
          <Input
            validateOn="blur"
            name="city"
            placeholder={t('common:auth.registration.form.city')}
            validate={required}
            disabled={notEmpty(user?.city)}
          />
        </FormGroup>
        <FormGroup title={t('common:auth.registration.form.address')}>
          <Input
            validateOn="blur"
            name="address"
            placeholder={t('common:auth.registration.form.address')}
            validate={required}
            disabled={notEmpty(user?.address)}
          />
        </FormGroup>
        <FormGroup title={t('common:auth.registration.form.postalCode')}>
          <Input
            validateOn="blur"
            name="postalCode"
            placeholder={t('common:auth.registration.form.postalCode')}
            validate={required}
            disabled={notEmpty(user?.postalCode)}
          />
        </FormGroup>
      </ScFormRegistration>
      <ScRegistrationWrapButtons>
        <Button blackTransparent large onClick={previous} noMinWidth wide>
          {t('auth.registration.buttons.back')}
        </Button>
        <Button
          type="submit"
          primary
          onClick={handleFinishClick}
          isLoading={isLoading}
          large
          noMinWidth
          wide
        >
          {t('button.finish')}
        </Button>
      </ScRegistrationWrapButtons>
    </Multistep.Step>
  );
};
